import React, { useEffect, useState } from 'react';
import { Anchor, Box, Heading, PageContent, Text } from 'grommet';
import PropTypes from 'prop-types';
import { API } from '@enrich/shared-lib';
import QueryString from 'qs';

const MessageBadge = ({ label, background }) => (
    <Box
        background={background}
        pad={{ horizontal: 'xsmall', vertical: 'xxsmall' }}
        round="xsmall">
        <Text size="xsmall" color="dark-4">
            {label}
        </Text>
    </Box>
);

MessageBadge.propTypes = {
    label: PropTypes.string,
    background: PropTypes.string,
};

export const ContactMessages = ({ contactId, showConversationsLink = true }) => {
    const [messages, setMessages] = useState([]);

    useEffect(() => {
        getMessages();
    }, []);

    const getMessages = async () => {
        const query = QueryString.stringify({
            filters: {
                $or: [
                    {
                        sender: {
                            contact: contactId,
                        },
                    },
                    {
                        recipients: {
                            contact: contactId,
                        },
                    },
                ],
            },
            populate: {
                sender: {
                    populate: ['contact'],
                },
            },
            pagination: { limit: 3 },
            sort: ['createdAt:desc'],
        });

        try {
            const response = await API.get(`/api/messages?${query}`);
            setMessages(response.data.data);
        } catch (error) {
            console.error('Failed to load messages:', error);
        }
    };

    return (
        <PageContent>
            <Box>
                <Heading level="3">
                    {showConversationsLink ? (
                        <Anchor
                            label="Recent Messages"
                            href={`/contacts/${contactId}/conversations`}
                        />
                    ) : (
                        'Recent Messages'
                    )}
                </Heading>
                <Box width="medium" gap="small">
                    {messages.map((message) => {
                        return (
                            <Box key={message.id}>
                                <ContactMessage
                                    contactId={contactId}
                                    message={message}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </PageContent>
    );
};

export const ContactMessage = ({ contactId, message }) => {
    const formattedCreatedDate = new Date(
        message.attributes.createdAt,
    ).toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });

    const isFromContact =
        message.attributes.sender?.data?.attributes.contact.data.id ==
        contactId;
    const isAutomated = message.attributes.isFromKlaviyo;
    const isMassMessage = message.attributes.isMassMessage;
    const fromCompany = message.attributes.fromCompany;

    const firstName =
        message.attributes.sender?.data?.attributes.contact.data.attributes
            .firstName;

    const senderName =
        fromCompany || firstName || (isAutomated ? 'Automated' : null);

    return (
        <Box gap="xsmall" align={isFromContact ? 'start' : 'end'}>
            <Box
                background={isFromContact ? 'enrich-green-light' : null}
                pad="small"
                border={{ color: 'enrich-green', size: '2px' }}
                elevation="xsmall"
                style={{ whiteSpace: 'pre-wrap' }}
                round="small">
                <Box
                    direction="row"
                    justify="between"
                    align="center"
                    gap="small">
                    <Text weight="bold">{senderName}</Text>
                    <Box direction="row" gap="xsmall">
                        {isAutomated && (
                            <MessageBadge
                                label="Automated"
                                background="light-4"
                            />
                        )}
                        {isMassMessage && (
                            <MessageBadge
                                label="Mass Message"
                                background="light-3"
                            />
                        )}
                    </Box>
                </Box>
                <Box>
                    <Text
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                        }}
                        size="small">
                        {message.attributes.content}
                    </Text>
                </Box>
            </Box>
            <Box>
                <Text size="xsmall" color="grey" alignSelf="end">
                    {formattedCreatedDate}
                </Text>
            </Box>
        </Box>
    );
};

ContactMessages.propTypes = {
    contactId: PropTypes.string,
    showConversationsLink: PropTypes.bool,
};

ContactMessage.propTypes = {
    contactId: PropTypes.string,
    message: PropTypes.object,
};
